import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';

import { RelatorioService } from '@app/services/relatorio.service';
import { UtilitarioService } from '@app/services/utilitario.service';
import { CDSMask } from '@app/utils/cds-mask';
import { DemonstrativoResultado as DemonstrativoResultado } from './dr.model';
import { IdDescricao } from '@app/models/util/id-descricao';
import { ConsultasService } from '@app/services/consultas.service';
import { TreeModule } from '@circlon/angular-tree-component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-reldr',
  templateUrl: 'dr.component.html',
  styleUrls: ['dr.component.scss']
})
export class RelatorioDRComponent implements OnInit {

  cdsMask: CDSMask = new CDSMask();
  dre: DemonstrativoResultado[] = [];
  lstUnidade: IdDescricao[];
  lstConta: IdDescricao[] = [];
  lstPossuiFilhos = [];
  lstIdPais = [];
  tabelaExpandida: boolean = false;
  unidadeId?: number = null;
  dataInicial = '';
  dataFinal = '';
  regime = 'CAIXA';
  contaId = 0;
  pesquisando: boolean;
  dataRelatorio: Date;

  constructor(
    private relatorioService: RelatorioService,
    private consultasService: ConsultasService,
    @Inject(ToastrService) public toastr: ToastrService) {
    this.dataInicial = UtilitarioService.retornaDataPrimeiroDiaDoMes();
    this.dataFinal = UtilitarioService.retornaDataUltimoDiaDoMes();
  }

  ngOnInit() {
    this.consultasService.consultar('UNIDADE_C_RATEIO').subscribe(data => this.lstUnidade = data);
    this.consultasService.consultar('CONTA').subscribe(data => this.lstConta = data);
    this.obterDRE();
  }

  obterDRE() {
    this.dataRelatorio = new Date();
    this.pesquisando = true;
    this.relatorioService.dr(this.unidadeId, this.dataInicial, this.dataFinal, this.regime, this.contaId).toPromise()
      .then((data: DemonstrativoResultado[]) => {
        this.dre = data;
        // Zerado a cada pesquisa: sem isso os ids da consulta anterior
        // permanecem e marcam PossuiFilhos em contas que não têm filhos,
        // deixando chevron em linha que não abre.
        this.lstPossuiFilhos = [];
        this.lstIdPais = [];
        this.dre.forEach(item => {
          item.MostrarFilhos = false;
          item.PossuiFilhos = false;
          if (item.GrupoId != null) {
            this.lstPossuiFilhos.push(item.GrupoId);
          }
        });
        this.lstPossuiFilhos = this.lstPossuiFilhos.filter((v, i, a) => a.indexOf(v) === i);
        this.dre.forEach(item => {
          if (this.lstPossuiFilhos.includes(item.Id)) {
            item.PossuiFilhos = true;
          }
        });
        this.resolverTipos();
        if (this.dre.length < 1) {
          this.toastr.info("Busca não encontrou resultados");
        }
        this.pesquisando = false;
      }, erro => {
        this.pesquisando = false
        this.toastr.warning("Realize a pesquisa novamente.")
      })
    //  () => this.pesquisando = false);
  }

  /**
   * Define Tipo ('R'/'D') de cada linha para o realce de receita/despesa.
   * O endpoint não garante ReceitaDespesa preenchido em todos os níveis, então:
   * 1) usa a marcação da própria linha; 2) herda do primeiro ancestral marcado;
   * 3) deduz pela descrição do grupo raiz. Sem os três, fica null (neutro).
   */
  private resolverTipos() {
    const porId = new Map<number, DemonstrativoResultado>();
    this.dre.forEach(item => porId.set(item.Id, item));

    this.dre.forEach(item => {
      let tipo = normalizarMarcacao(item.ReceitaDespesa);
      let raiz = item;

      while (!tipo && raiz.GrupoId != null) {
        const pai = porId.get(Number(raiz.GrupoId));
        if (!pai) { break; }
        raiz = pai;
        tipo = normalizarMarcacao(raiz.ReceitaDespesa);
      }

      item.Tipo = tipo ? tipo : deduzirPelaDescricao(raiz.Descricao);
    });

    function normalizarMarcacao(marcacao: string): string {
      if (!marcacao) { return null; }
      const valor = marcacao.trim().toUpperCase();
      if (valor.startsWith('R')) { return 'R'; }
      if (valor.startsWith('D')) { return 'D'; }
      return null;
    }

    function deduzirPelaDescricao(descricao: string): string {
      const texto = (descricao || '').toUpperCase();
      if (/RECEIT|FATURAMENT|VENDA|ENTRADA/.test(texto)) { return 'R'; }
      if (/DESPES|CUSTO|DEDU|IMPOST|SA[IÍ]DA/.test(texto)) { return 'D'; }
      return null;
    }
  }

  public obterPais() {
    this.dre.filter(x => x.GrupoId == null).forEach(e => this.lstIdPais.push(e.Id));
    return this.dre.filter(x => x.GrupoId == null);
  }

  public obterFilhos(id) {
    return this.dre.filter(x => x.GrupoId === id);
  }

  MostrarFilhos(id) {
    this.dre.filter(x => x.Id === id).forEach(e => {
      e.MostrarFilhos = e.MostrarFilhos ? false : true;
    });
  }

  expandirTabela() {
    this.tabelaExpandida = !this.tabelaExpandida;
  }

  @ViewChild('relatorioExpandido') relatorioExpandido: ElementRef;
  public imprimirRelatorioExpandido() {
    UtilitarioService.imprimirNode(this.relatorioExpandido.nativeElement, "Relatório Demonstrativo de Resultados", this.montaImpressaoFiltros())
  }

  @ViewChild('relatorioMinimizado') relatorioMinimizado: ElementRef;
  public imprimirRelatorioMinimizado() {
    let conteudo = document.getElementById("demostrativoDeResultado").innerHTML;
    UtilitarioService.imprimirHTML(conteudo, "Relatório Demonstrativo de Resultados", this.montaImpressaoFiltros())
  }

  private montaImpressaoFiltros(){
    let objetoFiltros = new Map<string, string>();

    adicionaMapValido('Emitido em', this.dataRelatorio.toLocaleString());
    adicionaMapValido('Regime', this.regime === 'COMPETENCIA' ? 'Competência' : 'Caixa');

    parseDateAdicionaMap('Data Inicial', this.dataInicial);
    parseDateAdicionaMap('Data Final', this.dataFinal);

    obtemDescricaoAdicionaMap('Unidade', this.lstUnidade, this.unidadeId);
    obtemDescricaoAdicionaMap('Conta', this.lstConta, this.contaId);

    function obtemDescricaoAdicionaMap(chave: string, listaItens: IdDescricao[], idItem: number){
      if(idItem){
        let valor = listaItens.find(x => x.Id === idItem).Descricao;
        objetoFiltros.set(chave, valor);
      }
    }

    function parseDateAdicionaMap(tipoData: string, data: string){
      if(data && data.indexOf('-') >= 0){
        let dataPartes = data.split('-');
        let dataMontada = dataPartes[2] + '/' + dataPartes[1] + '/' + dataPartes[0];
        objetoFiltros.set(tipoData, dataMontada);
      }
    }

    function adicionaMapValido(chave: string, valor: any){
      if(valor){
        objetoFiltros.set(chave, valor.toString());
      }
    }

    let stringHtml = '';

    objetoFiltros.forEach((valor, chave) => {
      stringHtml += `<tr><td>${chave}</td><td>${valor}</td></tr>`;
    })
    return stringHtml;
  }
}

